"use client"

import {
  Music2,
  Play,
  Radio,
} from "lucide-react"
import {
  motion,
} from "framer-motion"

import type {
  RecentActivityItem,
} from "./recent-activity.types"

type Props = {
  item: RecentActivityItem
  index: number
  isCurrent: boolean
  isPlaying: boolean
  onPlay: (
    item: RecentActivityItem,
  ) => void
  relativeTime: string
}

export default function RecentActivityItemRow({
  item,
  index,
  isCurrent,
  isPlaying,
  onPlay,
  relativeTime,
}: Props) {
  const isActive =
    isCurrent && isPlaying

  return (
    <motion.button
      type="button"
      onClick={() => onPlay(item)}
      initial={{
        opacity: 0,
        y: 6,
      }}
      animate={{
        opacity: 1,
        y: 0,
      }}
      exit={{
        opacity: 0,
        scale: 0.98,
      }}
      transition={{
        duration: 0.2,
        delay: index * 0.015,
      }}
      className="
        group
        relative
        flex
        w-full
        items-center
        gap-2
        rounded-2xl
        px-2
        py-2
        text-left
        outline-none
        transition-all
        duration-200
        hover:bg-white/[.035]
        active:scale-[.995]
        focus-visible:bg-white/[.04]
      "
    >
      {/* =====================================================
          COVER
      ===================================================== */}

      <div
        className="
          relative
          h-12
          w-12
          shrink-0
          overflow-hidden
          rounded-lg
          bg-white/[.045]
          ring-1
          ring-white/[.06]
        "
      >
        {item.image ? (
          <img
            src={item.image}
            alt=""
            className="
              h-full
              w-full
              object-cover
              transition-transform
              duration-500
              group-hover:scale-[1.04]
            "
          />
        ) : (
          <div
            className="
              flex
              h-full
              w-full
              items-center
              justify-center
            "
          >
            <Music2
              size={16}
              strokeWidth={1.7}
              className="text-white/25"
            />
          </div>
        )}

        {/* PLAY */}

        <div
          className={`
            absolute
            inset-0
            flex
            items-center
            justify-center
            bg-black/45
            backdrop-blur-[1px]
            transition-opacity
            duration-200
            ${
              isActive
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100"
            }
          `}
        >
          {isActive ? (
            <div
              className="
                flex
                h-4
                items-end
                gap-[2px]
              "
            >
              <span
                className="
                  h-2
                  w-[2px]
                  animate-pulse
                  rounded-full
                  bg-white
                "
              />

              <span
                className="
                  h-4
                  w-[2px]
                  animate-pulse
                  rounded-full
                  bg-white
                  [animation-delay:120ms]
                "
              />

              <span
                className="
                  h-2.5
                  w-[2px]
                  animate-pulse
                  rounded-full
                  bg-white
                  [animation-delay:240ms]
                "
              />
            </div>
          ) : (
            <Play
              size={14}
              fill="currentColor"
              strokeWidth={1.5}
              className="text-white"
            />
          )}
        </div>
      </div>

      {/* =====================================================
          INFORMATION
      ===================================================== */}

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <p
            className={`
              min-w-0
              truncate
              text-[11px]
              font-medium
              leading-tight
              tracking-[-0.01em]
              ${
                isCurrent
                  ? "text-white"
                  : "text-white/75"
              }
            `}
          >
            {item.title}
          </p>

          {item.type === "radio" && (
            <Radio
              size={10}
              strokeWidth={1.8}
              className="shrink-0 text-white/40"
            />
          )}

          {isActive && (
            <span
              className="
                h-1.5
                w-1.5
                shrink-0
                rounded-full
                bg-[var(--primary-color)]
                shadow-[0_0_8px_var(--primary-color)]
              "
            />
          )}
        </div>

        <div
          className="
            mt-1
            flex
            min-w-0
            items-center
            gap-1.5
          "
        >
          <p
            className="
              min-w-0
              truncate
              text-[9px]
              leading-none
              text-white/50
            "
          >
            {item.artist}
          </p>

          {item.playlistTitle && (
            <>
              <span className="text-[8px] text-white/10">
                ·
              </span>

              <p
                className="
                  min-w-0
                  truncate
                  text-[8px]
                  leading-none
                  text-white/30
                "
              >
                {item.playlistTitle}
              </p>
            </>
          )}
        </div>
      </div>

      {/* =====================================================
          TIME
      ===================================================== */}

      <span
        className="
          hidden
          shrink-0
          text-[8px]
          tabular-nums
          text-white/40
          sm:block
        "
      >
        {relativeTime}
      </span>

      {/* =====================================================
          SUBTLE ACTIVE GLOW
      ===================================================== */}

      {isCurrent && (
        <motion.div
          layoutId="recent-active-dot"
          className="
            absolute
            right-2
            top-1/2
            h-1
            w-1
            -translate-y-1/2
            rounded-full
            bg-[var(--primary-color)]
            shadow-[0_0_10px_var(--primary-color)]
          "
        />
      )}
    </motion.button>
  )
}